// Libs
import { Document, Page, Text, View } from '@react-pdf/renderer';
// Components, Layouts, Pages
import {
  ContactUsPdf,
  HeaderPdf,
  InformationPdf,
  Logo,
  ProductTable,
  Space,
  TotalPdf,
} from '~/components/module/pdf';
// Others
import { IEstimateDetailUpdate, IMaterialEstimate, IServiceEstimate } from '~/utils/interface/estimate';
import { formatCurrency, getFullName } from '~/utils/helper/common';
import { DEFAULT_CURRENCY } from '~/utils/constants/common';
import { IColTablePdf } from '../../pdf/productTable/ProductTable';
// Styles, images, icons
import { styles } from './styles';

type Props = {
  data: IEstimateDetailUpdate;
};

const SendPdfEstimate = (props: Props) => {
  //#region Destructuring Props
  const { data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const columnsService = (): IColTablePdf<IServiceEstimate>[] => {
    return [
      {
        key: 'name',
        title: 'Service Name',
        render(record, index) {
          return <Text>{record.name}</Text>;
        },
      },
      {
        key: 'quantity',
        title: 'Quantity',
        render(record, index) {
          return <Text>{record.quantity ?? 0}</Text>;
        },
      },
      {
        key: 'price',
        title: 'Price',
        render(record, index) {
          return <Text>{record.price ? formatCurrency(record.price) : DEFAULT_CURRENCY}</Text>;
        },
      },
      {
        key: 'amount',
        title: 'Amount',
        render(record, index) {
          return <Text>{record.totalPrice ? formatCurrency(record.totalPrice) : DEFAULT_CURRENCY}</Text>;
        },
      },
    ];
  };

  const columnsMaterial = (): IColTablePdf<IMaterialEstimate>[] => {
    return [
      {
        key: 'name',
        title: 'Material Name',
        render(record, index) {
          return <Text>{record.name}</Text>;
        },
      },
      {
        key: 'quantity',
        title: 'Quantity',
        render(record, index) {
          return <Text>{record.quantity ?? 0}</Text>;
        },
      },
      {
        key: 'price',
        title: 'Price',
        render(record, index) {
          return <Text>{record.price ? formatCurrency(record.price) : DEFAULT_CURRENCY}</Text>;
        },
      },
      {
        key: 'amount',
        title: 'Amount',
        render(record, index) {
          return <Text>{record.totalPrice ? formatCurrency(record.totalPrice) : DEFAULT_CURRENCY}</Text>;
        },
      },
    ];
  };

  const servicesSubtotal =
    data.lineItems?.services?.reduce((acc, service) => {
      return acc + (Number(service.totalPrice) || 0);
    }, 0) ?? 0;

  const materialsSubtotal =
    data.lineItems?.materials?.reduce((acc, material) => {
      return acc + (Number(material.totalPrice) || 0);
    }, 0) ?? 0;
  //#endregion Handle Function

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.container}>
          <View style={styles.viewHeader}>
            <Logo />
            <HeaderPdf
              title='Estimate'
              id={data.id}
              titleDate='Estimate Date:'
              date={new Date().toDateString()}
            />
          </View>

          <View style={styles.viewInformation}>
            <View style={styles.information}>
              <InformationPdf
                title='Estimate Information'
                customerName={getFullName({ ...data.client })}
                email={data.client?.email}
                total={data.lineItems?.total}
              />
            </View>

            <ContactUsPdf />
          </View>

          <Space height={20} />

          <Text style={styles.titleTable}>Line Items</Text>

          <View style={styles.tableWrap}>
            {data.lineItems?.services && data.lineItems?.services.length > 0 && (
              <View style={styles.tableWrap}>
                <ProductTable columns={columnsService()} data={data.lineItems?.services ?? []} />

                <View style={styles.total}>
                  <Text style={styles.textTotal}>Services Subtotal: </Text>
                  <Text style={styles.textSubTotal}>{formatCurrency(servicesSubtotal)}</Text>
                </View>
              </View>
            )}

            {data.lineItems?.materials && data.lineItems?.materials.length > 0 && (
              <View style={styles.tableWrap}>
                <ProductTable columns={columnsMaterial()} data={data.lineItems?.materials ?? []} />

                <View style={styles.total}>
                  <Text style={styles.textTotal}>Materials Subtotal: </Text>
                  <Text style={styles.textSubTotal}>{formatCurrency(materialsSubtotal)}</Text>
                </View>
              </View>
            )}
          </View>

          <Space height={50} />

          <TotalPdf total={data.lineItems?.total} subtotal={data.lineItems?.subtotal} tax='0' />

          <View style={styles.viewFooter}>
            <Text style={styles.textFooter}>
              Thank you for choosing Noble Plumbing to take care of all your plumbing needs.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default SendPdfEstimate;
